"use client"

import { useCallback, useRef, useState } from "react"
import { Settings as SettingsIcon } from "lucide-react"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { useWorkflow } from "@/context/workflow-context-simple"
import { useVmsConfig, type VmsSubTab } from "@/hooks/vms/use-vms-config"
import { useLocalizedField } from "@/utils/language-utils"
import { VmsSettingsPanel } from "@/components/vms/vms-settings-panel"
import { VmsSubTabContent } from "@/components/vms/vms-sub-tab-content"

/**
 * VMS Placements tab — renders Requests/Emails sub-tabs styled like the
 * WorkOrder stage strip (dark-blue text + parenthesised counts) inside the
 * grid header. Settings is a circular icon button placed after Edit columns
 * (before Filter) in the right action group, matching the ATS tab order.
 */
/**
 * Optional controlled props for the VMS Settings panel. Host (workflow-hub.tsx)
 * lifts the open state so the per-Request-tab kebab in the global top-right
 * slot can drive the same modal the in-tab circular settings icon drives —
 * one open state, two triggers. Both props are optional: when neither is
 * passed the tab falls back to its own internal useState (the legacy
 * standalone-tab behaviour), which keeps storybook / standalone-rendering
 * usages working without host changes.
 */
interface VmsPlacementsTabProps {
  isSettingsOpen?: boolean
  onSettingsOpenChange?: (open: boolean) => void
}

export function VmsPlacementsTab({ isSettingsOpen: extSettingsOpen, onSettingsOpenChange }: VmsPlacementsTabProps = {}) {
  const containerRef = useRef<HTMLDivElement>(null)
  // Controlled/uncontrolled toggle for the settings panel. When the host
  // passes either of the controlled props we honour both; otherwise we
  // own the state internally.
  const [internalSettingsOpen, setInternalSettingsOpen] = useState(false)
  const isSettingsOpen = extSettingsOpen ?? internalSettingsOpen
  const setIsSettingsOpen = onSettingsOpenChange ?? setInternalSettingsOpen
  const [activeEntityType, setActiveEntityType] = useState<VmsSubTab["entityType"] | null>(null)
  // When a VmsEmail row's "View Linked Request" kebab fires, the host needs
  // two things in lock-step: (1) switch the active sub-tab to VmsRequest,
  // and (2) tell that sub-tab's WorkflowProvider to scope the grid to a
  // single id. The id is staged here and consumed by VmsSubTabContent on
  // the very next render via PendingFocusApplier. Once the bridge calls
  // onPendingFocusConsumed we null it so re-renders don't re-apply the
  // filter (the user can clear it via the grid's Clear Filters control).
  const [pendingFocusRequestId, setPendingFocusRequestId] = useState<string | null>(null)
  // Friendly label that ends up in the dismissible filter chip — typically the
  // subject of the source email so the user sees "Showing: Re: New Engineer
  // Request for ACME Corp" instead of the raw OData clause.
  const [pendingFocusRequestLabel, setPendingFocusRequestLabel] = useState<string | null>(null)
  // Filtered total reported by each sub-tab's grid the last time it was the
  // active tab. Keyed by entityType so switching tabs doesn't smear one tab's
  // count onto the other. Once a tab has been visited, we keep using its
  // last-known filtered count (4 with ShowCompleted-off, etc.) even after the
  // user clicks away — otherwise the inactive tab would fall back to the
  // mount-time unfiltered total (5) and the badge would flip-flop between the
  // two values every time the user switches tabs.
  const [filteredCounts, setFilteredCounts] = useState<
    Partial<Record<VmsSubTab["entityType"], number>>
  >({})
  const { configuration } = useWorkflow()
  const { subTabs, isLoading, error } = useVmsConfig()
  const getLocalizedField = useLocalizedField()

  // Resolve "Can view VMS Settings?" by unioning authorizedActions from every
  // config the user could have it granted on:
  //   1. The active WorkOrder configuration (legacy: many tenants seeded
  //      ViewVmsSettings here before VMS got its own entity types).
  //   2. The VmsRequest / VmsEmail configurations returned by useVmsConfig
  //      (the semantically-correct home for a VMS-tab permission going
  //      forward — admins configuring VMS for a new tenant grant it here).
  // Either source is sufficient; we don't require both. This keeps existing
  // WorkOrder-configured tenants working and makes VMS-side configuration
  // honour the same gate without a backfill migration.
  const toActionCodes = (cfg: { authorizedActions?: any[] } | null | undefined) =>
    (cfg?.authorizedActions || []).map((a: any) =>
      typeof a === "string" ? a : a.actionCode
    )
  const authorizedActionUnion = new Set<string>([
    ...toActionCodes(configuration),
    ...subTabs.flatMap((t) => toActionCodes(t.configuration)),
  ])
  const canViewVmsSettings = authorizedActionUnion.has("ViewVmsSettings")

  const effectiveEntityType =
    activeEntityType ?? (subTabs.length > 0 ? subTabs[0].entityType : null)

  // Stable callback so the inner TotalCountReporter's effect doesn't loop.
  // Receives entityType from the reporter so both mounted sub-tabs can report
  // their own counts in parallel without stepping on each other — without
  // this, the inactive provider's reports would land on whichever tab is
  // currently active and corrupt the badge.
  const handleTabTotalCount = useCallback(
    (entityType: VmsSubTab["entityType"], count: number) => {
      setFilteredCounts((prev) =>
        prev[entityType] === count ? prev : { ...prev, [entityType]: count }
      )
    },
    []
  )

  // Fired by WorkflowGrid (inside the VmsEmail sub-tab) when its dispatch
  // handler unboxes the open_linked_request action. Switching tabs alone is
  // not enough — the VmsRequest sub-tab is already mounted with its own
  // provider state, so we additionally stage the id (and a friendly label
  // derived from the email row's title) for the bridge component to apply
  // as a temporary `id eq` filter on the next render.
  const handleOpenLinkedRequest = useCallback((requestId: string, itemTitle?: string) => {
    setPendingFocusRequestId(requestId)
    setPendingFocusRequestLabel(itemTitle?.trim() || null)
    setActiveEntityType("VmsRequest")
  }, [])

  const handlePendingFocusConsumed = useCallback(() => {
    setPendingFocusRequestId(null)
    setPendingFocusRequestLabel(null)
  }, [])

  // Build the sub-tab list passed to the selector, preferring the cached
  // filtered total (from when the tab was last active) over the mount-time
  // unfiltered count. Applies to both active and inactive tabs so the badge
  // stays consistent — without this, switching to Emails would flip the
  // Requests badge back to its unfiltered mount-time value, then flip again
  // when the user returned to Requests.
  const subTabsForSelector: VmsSubTab[] = subTabs.map((t) => {
    const override = filteredCounts[t.entityType]
    if (typeof override === "number") {
      return { ...t, count: override }
    }
    return t
  })

  const settingsButton = canViewVmsSettings ? (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            onClick={() => setIsSettingsOpen(true)}
            aria-label="VMS Settings"
            style={{
              width: 40,
              height: 40,
              borderRadius: 9999,
              border: "1px solid #BFC6D7",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "#fff",
              cursor: "pointer",
              color: "#0F214B",
            }}
          >
            <SettingsIcon size={20} strokeWidth={1.6} />
          </button>
        </TooltipTrigger>
        <TooltipContent>
          <p>VMS Settings</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  ) : null


  return (
    <div ref={containerRef} className="flex flex-col h-full overflow-hidden">
      {isLoading ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-400 mx-auto" />
            <p className="text-xs text-gray-400 mt-2">Loading VMS configuration…</p>
          </div>
        </div>
      ) : error ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <p className="text-sm text-red-500">Failed to load VMS configuration</p>
            <p className="text-xs text-gray-400 mt-1">{error}</p>
          </div>
        </div>
      ) : subTabs.length === 0 ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <p className="text-sm text-gray-400">No VMS configurations available</p>
            <p className="text-xs text-gray-300 mt-1">
              Run the Default-doc init for entityType=VmsRequest and entityType=VmsEmail.
            </p>
          </div>
        </div>
      ) : effectiveEntityType ? (
        // Mount BOTH sub-tabs at once and hide the inactive one with
        // display:none. Each <VmsSubTabContent> owns its own WorkflowProvider
        // — toggling visibility instead of unmounting keeps every
        // entity-typed fetch (configurations, views, assigned-users, the
        // initial search) and the per-tab filter/pagination/selection state
        // alive across switches. First paint pays the cost of fetching both
        // tabs in parallel (each grid's search also supplies its pill count,
        // so no dedicated count calls); flipping pills afterwards is free.
        //
        // Header chrome (the sub-tab selector + settings button) is rendered
        // ONLY into the active tab's grid header so we don't double-mount
        // click handlers or stack two selectors on top of each other.
        subTabs.map((tabItem) => {
          const isActive = tabItem.entityType === effectiveEntityType
          return (
            <div
              key={tabItem.entityType}
              style={{
                display: isActive ? "flex" : "none",
                flex: 1,
                flexDirection: "column",
                minHeight: 0,
                overflow: "hidden",
              }}
            >
              <VmsSubTabContent
                subTab={tabItem}
                isActiveSubTab={isActive}
                headerTitleSlot={
                  isActive ? (
                    <VmsSubTabSelector
                      subTabs={subTabsForSelector}
                      activeEntityType={effectiveEntityType}
                      onSelect={setActiveEntityType}
                      getLocalizedField={getLocalizedField}
                    />
                  ) : null
                }
                headerExtraControls={isActive ? settingsButton : null}
                onTotalCountChange={handleTabTotalCount}
                onOpenLinkedRequest={
                  tabItem.entityType === "VmsEmail"
                    ? handleOpenLinkedRequest
                    : undefined
                }
                pendingFocusId={
                  tabItem.entityType === "VmsRequest"
                    ? pendingFocusRequestId
                    : null
                }
                pendingFocusLabel={
                  tabItem.entityType === "VmsRequest"
                    ? pendingFocusRequestLabel
                    : null
                }
                onPendingFocusConsumed={
                  tabItem.entityType === "VmsRequest"
                    ? handlePendingFocusConsumed
                    : undefined
                }
              />
            </div>
          )
        })
      ) : null}

      {isSettingsOpen && (
        <VmsSettingsPanel onClose={() => setIsSettingsOpen(false)} />
      )}
    </div>
  )
}

/**
 * Text-style tab strip for the VMS sub-tabs — mirrors the WorkOrder stage
 * row treatment: dark blue text, count in muted parentheses, the active tab
 * gets bold weight + an orange underline. No background pill.
 */
function VmsSubTabSelector({
  subTabs,
  activeEntityType,
  onSelect,
  getLocalizedField,
}: {
  subTabs: VmsSubTab[]
  activeEntityType: VmsSubTab["entityType"] | null
  onSelect: (entityType: VmsSubTab["entityType"]) => void
  getLocalizedField: (record: any, base: string) => string
}) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 24 }}>
      {subTabs.map((tab) => {
        const isActive = activeEntityType === tab.entityType
        const title = tab.stage ? getLocalizedField(tab.stage, "title") : tab.entityType
        return (
          <button
            key={tab.entityType}
            onClick={() => onSelect(tab.entityType)}
            style={{
              background: "transparent",
              border: "none",
              padding: "4px 0",
              cursor: "pointer",
              color: "#0F214B",
              fontSize: 18,
              fontWeight: isActive ? 700 : 400,
              borderBottom: isActive ? "2px solid #F59E42" : "2px solid transparent",
              display: "inline-flex",
              alignItems: "baseline",
              gap: 6,
            }}
          >
            <span>{title}</span>
            {tab.count !== null && (
              <span style={{ fontWeight: 400, fontSize: 16, color: "#9795B5" }}>
                ({tab.count})
              </span>
            )}
          </button>
        )
      })}
    </div>
  )
}
